/**
 * Admin Portal → Partners: the code list (filter, export, revoke, extend/limit) and the
 * two ways to make codes on a tag — its shared code and a batch of single-use codes.
 *
 * GET /codes is open to every admin role; the CSV export and every write need an admin
 * or super admin session (the server answers 403 otherwise, and these controls hide).
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md "Offers and codes".
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  CODE_STATES,
  CODE_STATE_LABELS,
  codeEditable,
  codeStateBadge,
  endOfDayIso,
  sharedCodeBody,
  singleUseBody,
  type CodeFilters,
  type CodeRow,
  type SingleUseForm,
  type TagRow,
} from './partnersLogic';
import {
  NoticeLine,
  StatusBadge,
  TypedConfirm,
  copyText,
  errText,
  fmtDate,
  hint,
  SAVE_FAILED,
  saveBlob,
  input,
  label,
  row,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';
import { NOTES_MAX_LEN, PURPOSE_MAX_LEN, batchMeta, shortId } from '../codes/codesAdminLogic';
import { FAMILY_VOCAB_NOTE } from '../codes/codesAreaLogic';

const PAGE = 100;

export const PartnerCodesPanel: React.FC<{
  partnerId: string;
  tags: TagRow[];
  fixedTagId?: string;
  writable: boolean;
  refreshKey?: number;
}> = ({ partnerId, tags, fixedTagId, writable, refreshKey }) => {
  const [filters, setFilters] = useState<CodeFilters>({ partner_id: partnerId, tag_id: fixedTagId, kind: '', state: '', q: '', external_ref: '' });
  const [page, setPage] = useState(0);
  const [codes, setCodes] = useState<CodeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const [revoking, setRevoking] = useState<CodeRow | null>(null);
  const [editing, setEditing] = useState<{ row: CodeRow; expiresOn: string; neverExpires: boolean; limit: string } | null>(null);

  // The parent keys this panel by partner (and by tag inside a tag's detail), so the
  // initial filters above are the partner's and tag's for the panel's whole life.

  const load = useCallback(async () => {
    try {
      const res = await PartnersAdmin.listCodes({ ...filters, limit: PAGE, offset: page * PAGE });
      setCodes(res.codes || []);
      setTotal(res.total || 0);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportCsv = () =>
    run(async () => {
      try {
        const file = await PartnersAdmin.exportCodes(filters);
        if (!saveBlob(file.blob, file.filename)) {
          setNotice({ kind: 'error', text: SAVE_FAILED });
          return;
        }
        setNotice({ kind: 'ok', text: `Exported ${total} code${total === 1 ? '' : 's'} (the file an admin hands the partner: codes, no user data).` });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const revoke = () =>
    run(async () => {
      if (!revoking) return;
      try {
        const res = await PartnersAdmin.revokeCode(revoking.id);
        setNotice({ kind: 'ok', text: res.changed ? `Revoked ${revoking.code}. It can never be redeemed again; an offer already redeemed stays with that person.` : `${revoking.code} was already revoked.` });
        setRevoking(null);
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const saveEdit = () =>
    run(async () => {
      if (!editing) return;
      const body: Record<string, unknown> = {};
      const errs: string[] = [];
      if (editing.neverExpires) {
        if (editing.row.kind === 'single_use') errs.push('A single-use code must expire (at most 730 days out).');
        else if (editing.row.expires_at !== null) body.expires_at = null;
      } else if (editing.expiresOn) {
        const { iso, error } = endOfDayIso(editing.expiresOn);
        if (error) errs.push(`Expires: ${error}`);
        else body.expires_at = iso;
      }
      if (editing.row.kind === 'shared') {
        const t = editing.limit.trim();
        const next = t === '' ? null : Number(t);
        if (t !== '' && (!/^\d+$/.test(t) || Number(t) < 1)) errs.push('Limit: 1 or more, blank = unlimited.');
        else if (next !== editing.row.max_redemptions) body.max_redemptions = next;
      }
      if (errs.length) { setNotice({ kind: 'error', text: 'Not saved:', extra: errs }); return; }
      if (!Object.keys(body).length) { setEditing(null); return; }
      try {
        const res = await PartnersAdmin.updateCode(editing.row.id, body);
        setNotice({ kind: 'ok', text: `Saved ${res.code.code}: ${Object.keys(res.changed || {}).join(', ') || 'no change'}.` });
        setEditing(null);
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const tagName = (id: string) => tags.find((t) => t.id === id)?.tag ?? '';

  return (
    <AdminCard>
      <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Codes{fixedTagId ? ` — ${tagName(fixedTagId)}` : ''}</h3>
      {/* Which status words this list speaks (review of #4276, NIT-4): the partner program's own. */}
      <p data-testid="vocab-note" style={{ ...hint, marginTop: 0 }}>{FAMILY_VOCAB_NOTE}</p>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={{ ...row, marginBottom: 10 }}>
        {!fixedTagId && (
          <div>
            <label style={label} htmlFor="cf-tag">Tag</label>
            <select id="cf-tag" style={input} value={filters.tag_id ?? ''} onChange={(e) => { setPage(0); setFilters({ ...filters, tag_id: e.target.value || undefined }); }}>
              <option value="">All tags</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={label} htmlFor="cf-kind">Kind</label>
          <select id="cf-kind" style={input} value={filters.kind ?? ''} onChange={(e) => { setPage(0); setFilters({ ...filters, kind: e.target.value as CodeFilters['kind'] }); }}>
            <option value="">Any</option>
            <option value="shared">Shared</option>
            <option value="single_use">Single-use</option>
          </select>
        </div>
        <div>
          <label style={label} htmlFor="cf-state">State</label>
          <select id="cf-state" style={input} value={filters.state ?? ''} onChange={(e) => { setPage(0); setFilters({ ...filters, state: e.target.value as CodeFilters['state'] }); }}>
            <option value="">Any</option>
            {CODE_STATES.map((s) => <option key={s} value={s}>{CODE_STATE_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="cf-q">Code starts with</label>
          <input id="cf-q" style={{ ...input, width: 140 }} value={filters.q ?? ''} onChange={(e) => { setPage(0); setFilters({ ...filters, q: e.target.value }); }} />
        </div>
        <div>
          <label style={label} htmlFor="cf-ref">Partner reference</label>
          <input id="cf-ref" style={{ ...input, width: 160 }} value={filters.external_ref ?? ''} onChange={(e) => { setPage(0); setFilters({ ...filters, external_ref: e.target.value }); }} />
        </div>
        {writable && <AdminButton size="sm" variant="secondary" onClick={exportCsv} disabled={busy || total === 0}>Export CSV</AdminButton>}
      </div>
      <div style={hint}>{total} code{total === 1 ? '' : 's'} match.</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Code</th><th style={th}>Tag</th><th style={th}>Kind</th><th style={th}>State</th>
              <th style={th}>Reference</th><th style={th}>Used</th><th style={th}>Expires</th><th style={th}>Created</th>
              {writable && <th style={th} />}
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td style={td} colSpan={writable ? 9 : 8}>No codes.</td></tr>}
            {codes.map((c) => (
              <tr key={c.id}>
                <td style={td}><code>{c.code}</code> <button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11 }} onClick={() => copyText(c.code)}>copy</button></td>
                <td style={td}>{c.tag}</td>
                <td style={td}>{c.kind === 'shared' ? 'Shared' : 'Single-use'}</td>
                <td style={td}><StatusBadge status={CODE_STATE_LABELS[c.state] ?? c.state} tone={codeStateBadge(c.state)} /></td>
                <td style={td}>{c.external_ref ?? '—'}</td>
                <td style={td}>{c.redemptions}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : c.kind === 'shared' ? ' / ∞' : ''}</td>
                <td style={td}>{c.expires_at ? fmtDate(c.expires_at) : 'never'}</td>
                <td style={td}>{fmtDate(c.created_at)}</td>
                {writable && (
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {codeEditable(c) && (
                      <>
                        <AdminButton size="sm" variant="ghost" onClick={() => setEditing({ row: c, expiresOn: '', neverExpires: false, limit: c.max_redemptions == null ? '' : String(c.max_redemptions) })}>
                          Extend / limit
                        </AdminButton>
                        <AdminButton size="sm" variant="ghost" onClick={() => setRevoking(c)}>Revoke</AdminButton>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > PAGE && (
        <div style={{ ...row, marginTop: 8 }}>
          <AdminButton size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</AdminButton>
          <span style={hint}>Page {page + 1} of {Math.ceil(total / PAGE)}</span>
          <AdminButton size="sm" variant="secondary" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</AdminButton>
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--surface-subtle, #f9fafb)', borderRadius: 10 }}>
          <b>{editing.row.code}</b> — extend the date or change the limit.
          <div style={{ ...row, marginTop: 8 }}>
            <div>
              <label style={label} htmlFor="ce-exp">New expiry date</label>
              <input id="ce-exp" type="date" style={input} value={editing.expiresOn} disabled={editing.neverExpires}
                onChange={(e) => setEditing({ ...editing, expiresOn: e.target.value })} />
            </div>
            {editing.row.kind === 'shared' && (
              <>
                <label style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={editing.neverExpires} onChange={(e) => setEditing({ ...editing, neverExpires: e.target.checked })} /> Never expires
                </label>
                <div>
                  <label style={label} htmlFor="ce-limit">Redemption limit (blank = unlimited)</label>
                  <input id="ce-limit" style={{ ...input, width: 120 }} value={editing.limit} onChange={(e) => setEditing({ ...editing, limit: e.target.value })} />
                </div>
              </>
            )}
            <AdminButton size="sm" onClick={saveEdit} disabled={busy}>Save</AdminButton>
            <AdminButton size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</AdminButton>
          </div>
          <div style={hint}>A single-use code can be extended up to 730 days out, never to "never". A limit cannot go below the redemptions already made.</div>
        </div>
      )}

      {revoking && (
        <TypedConfirm
          title={`Revoke ${revoking.code}?`}
          body={<>It can never be redeemed again, and its name stays taken. An offer someone already redeemed stays theirs.{revoking.kind === 'shared' ? ' A printed shared code stops working for everyone.' : ''}</>}
          expected={revoking.code}
          confirmLabel="Revoke"
          busy={busy}
          onConfirm={revoke}
          onCancel={() => setRevoking(null)}
        />
      )}
    </AdminCard>
  );
};

// ── making codes on one tag ──────────────────────────────────────────────────

export const TagCodeMaker: React.FC<{ tag: TagRow; onMade: () => void }> = ({ tag, onMade }) => {
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const [shared, setShared] = useState({ code: '', maxRedemptions: '', expiresOn: '' });
  const [single, setSingle] = useState<SingleUseForm>({ mode: 'refs', count: '10', refsText: '', prefix: '', expiresInDays: '90' });
  const [minted, setMinted] = useState<Array<{ code: string; external_ref: string | null; existing?: boolean }>>([]);
  // The batch these codes will be (migration 1877): what they are for, and notes. Optional.
  const [batch, setBatch] = useState({ purpose: '', notes: '' });
  const batchLine = (id: string | null | undefined) => (id ? ` Batch ${shortId(id)} (Code batches).` : '');

  const makeShared = () =>
    run(async () => {
      const { body, errors } = sharedCodeBody(shared);
      const meta = batchMeta(batch.purpose, batch.notes);
      if (errors.length || meta.errors.length) {
        setNotice({ kind: 'error', text: 'Not created:', extra: [...errors, ...meta.errors] });
        return;
      }
      try {
        const res = await PartnersAdmin.createSharedCode(tag.id, { ...body, ...meta.fields });
        const again = res.code.recreated_from?.length ? ' (the name was taken again from a revoked code of this tag)' : '';
        setNotice({ kind: 'ok', text: `Shared code ${res.code.code} is live${again}.${batchLine((res.code as { batch_id?: string | null }).batch_id)}` });
        setShared({ code: '', maxRedemptions: '', expiresOn: '' });
        onMade();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const mint = () =>
    run(async () => {
      const { body, errors } = singleUseBody(single);
      const meta = batchMeta(batch.purpose, batch.notes);
      if (errors.length || meta.errors.length) {
        setNotice({ kind: 'error', text: 'Not minted:', extra: [...errors, ...meta.errors] });
        return;
      }
      try {
        const res = await PartnersAdmin.mintSingleUse(tag.id, { ...body, ...meta.fields });
        setMinted(res.codes || []);
        setNotice({
          kind: 'ok',
          text: `${res.created} new code${res.created === 1 ? '' : 's'}; ${res.existing} reference${res.existing === 1 ? '' : 's'} already had an active code (answered, not doubled); ${res.replaced} replaced. Export the list to hand it over.${batchLine((res as { batch_id?: string | null }).batch_id)}`,
        });
        onMade();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const endedTag = tag.status === 'ended';
  return (
    <AdminCard variant="subtle">
      <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>Make codes for {tag.tag}</h4>
      <div style={hint}>A code carries this tag's offer ({tag.offer_summary || 'tracking only'}) and attributes the person to it. {endedTag ? 'This tag has ended: no new codes.' : ''}</div>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {!endedTag && (
        <div style={{ display: 'grid', gap: 14, marginTop: 8 }}>
          <div style={row}>
            <div>
              <label style={label} htmlFor="cb-purpose">Batch purpose (optional)</label>
              <input id="cb-purpose" style={{ ...input, width: 240 }} value={batch.purpose} maxLength={PURPOSE_MAX_LEN}
                placeholder="e.g. Instagram DMs, fall race bibs" onChange={(e) => setBatch({ ...batch, purpose: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={label} htmlFor="cb-notes">Batch notes (optional, never a person&apos;s details)</label>
              <input id="cb-notes" style={{ ...input, width: '100%' }} value={batch.notes} maxLength={NOTES_MAX_LEN}
                onChange={(e) => setBatch({ ...batch, notes: e.target.value })} />
            </div>
          </div>
          <div>
            <b style={{ fontSize: 13 }}>Shared code</b> <span style={hint}>— one printed code many people type (one active per tag).</span>
            <div style={{ ...row, marginTop: 6 }}>
              <div>
                <label style={label} htmlFor="sc-code">Code</label>
                <input id="sc-code" style={{ ...input, width: 160 }} value={shared.code} onChange={(e) => setShared({ ...shared, code: e.target.value })} placeholder="RUNCLUB" />
              </div>
              <div>
                <label style={label} htmlFor="sc-max">Limit (blank = unlimited)</label>
                <input id="sc-max" style={{ ...input, width: 110 }} value={shared.maxRedemptions} onChange={(e) => setShared({ ...shared, maxRedemptions: e.target.value })} />
              </div>
              <div>
                <label style={label} htmlFor="sc-exp">Expires (blank = never)</label>
                <input id="sc-exp" type="date" style={input} value={shared.expiresOn} onChange={(e) => setShared({ ...shared, expiresOn: e.target.value })} />
              </div>
              <AdminButton size="sm" onClick={makeShared} disabled={busy}>Create shared code</AdminButton>
            </div>
          </div>
          <div>
            <b style={{ fontSize: 13 }}>Single-use codes</b> <span style={hint}>— one per person; minting again for a reference answers the same code.</span>
            <div style={{ ...row, marginTop: 6 }}>
              <label style={{ fontSize: 13 }}><input type="radio" checked={single.mode === 'refs'} onChange={() => setSingle({ ...single, mode: 'refs' })} /> One per partner reference</label>
              <label style={{ fontSize: 13 }}><input type="radio" checked={single.mode === 'count'} onChange={() => setSingle({ ...single, mode: 'count' })} /> A number of codes</label>
            </div>
            <div style={{ ...row, marginTop: 6 }}>
              {single.mode === 'refs' ? (
                <div style={{ flex: 1, minWidth: 260 }}>
                  <label style={label} htmlFor="su-refs">The partner's references, one per line (e.g. their member or order ids)</label>
                  <textarea id="su-refs" rows={4} style={{ ...input, width: '100%' }} value={single.refsText} onChange={(e) => setSingle({ ...single, refsText: e.target.value })} />
                </div>
              ) : (
                <div>
                  <label style={label} htmlFor="su-count">How many (1–1,000)</label>
                  <input id="su-count" style={{ ...input, width: 100 }} value={single.count} onChange={(e) => setSingle({ ...single, count: e.target.value })} />
                </div>
              )}
              <div>
                <label style={label} htmlFor="su-prefix">Prefix (default: partner code)</label>
                <input id="su-prefix" style={{ ...input, width: 130 }} value={single.prefix} onChange={(e) => setSingle({ ...single, prefix: e.target.value })} />
              </div>
              <div>
                <label style={label} htmlFor="su-days">Expires in (days)</label>
                <input id="su-days" style={{ ...input, width: 90 }} value={single.expiresInDays} onChange={(e) => setSingle({ ...single, expiresInDays: e.target.value })} />
              </div>
              <AdminButton size="sm" onClick={mint} disabled={busy}>Mint</AdminButton>
            </div>
            {minted.length > 0 && (
              <div style={{ marginTop: 8, maxHeight: 180, overflow: 'auto', fontSize: 12 }}>
                {minted.map((m) => (
                  <div key={m.code}><code>{m.code}</code>{m.external_ref ? ` — ${m.external_ref}` : ''}{m.existing ? ' (already had this code)' : ''}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminCard>
  );
};
